'use client';

import { useEffect, useMemo } from 'react';
import { cn, getEvColorClass, EASE_PREMIUM, useBookCatalogContext, useTeaseAnalytics } from '@sharpr/ui';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, EyeOff } from 'lucide-react';
import type { SelectablePlayer } from '../../types/selection';
import type { MarketCode, Sport, BookLine } from '@sharpr/types';
import { getMarketFilterName, getMarketLongName, getMarketsForSport } from '@sharpr/types';
import type { OverlayTarget } from '../../types/research';
import { buildOverlayTarget } from '../../utils/buildOverlayTarget';
import { aggregateMarketLines } from '@/lib/research/buildResearchTarget';
import { BookLabel } from '../BookThumbnail';
import { useEvTease } from '../../contexts/EvTeaseContext';
import { formatEV, formatOdds } from '@sharpr/ui/format';

/**
 * Full-width brand-color CTA rendered above the per-book list in the mobile
 * card dropdown when the viewer's EV has been stripped server-side.
 */
function UnlockEvBanner() {
  // Mounted only when the card is expanded for a viewer without EV — mount = seen.
  const analytics = useTeaseAnalytics();
  useEffect(() => {
    analytics?.onView('mobile_expansion_banner');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per expansion
  }, []);
  return (
    <a
      href="/signup"
      onClick={() => analytics?.onClick('mobile_expansion_banner', 'cta', '/signup')}
      className={cn(
        'flex items-center justify-center gap-1.5 w-full rounded-lg px-3 py-2 shadow-sm',
        'text-sm font-semibold text-white',
        'bg-brand-500 hover:bg-brand-600 active:bg-brand-700',
        'dark:bg-brand-600 dark:hover:bg-brand-500 dark:active:bg-brand-700',
        'transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-bg-primary',
      )}
    >
      Start free trial to unlock EV
      <ChevronRight className="h-4 w-4" aria-hidden />
    </a>
  );
}

type MarketDenote = 'admin-only' | 'analyst-hidden' | null;

/** Muted market caption that sits above the book name on every row. Uses the
 *  full spelled-out name (e.g. "Total Bases") since the mobile expansion has
 *  room for it and the abbreviations read as cryptic. */
function MarketLabel({ market, denote }: { market: MarketCode; denote?: MarketDenote }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide text-gray-400 dark:text-text-tertiary leading-tight">
      {getMarketLongName(market)}
      {denote && (
        <span title={denote === 'analyst-hidden'
          ? 'Analyst-hidden — EV visible to admins only; publishes odds-only to subscribers'
          : 'EV visible to admins only — stripped for subscribers'}
        >
          <EyeOff
            className={cn('h-3 w-3', denote === 'analyst-hidden' ? 'text-gray-400 dark:text-text-tertiary' : 'text-amber-500')}
            aria-label={denote === 'analyst-hidden' ? 'Analyst-hidden market' : 'Admin-only EV'}
          />
        </span>
      )}
    </span>
  );
}

/** One `o{line} (odds)` / `u{line} (odds)` cell in the odds-only side stack. */
function SideOdds({ side, book }: { side: 'over' | 'under'; book: BookLine }) {
  return (
    <div className={cn('flex items-center gap-0.5 sm:gap-1', book.isRemoved && 'opacity-60')}>
      <span className="font-medium text-gray-900 dark:text-text-primary">
        {side === 'over' ? 'o' : 'u'}{book.line.toFixed(1)}
      </span>
      <span className="text-gray-400 dark:text-text-secondary">
        ({formatOdds(book.odds)})
      </span>
    </div>
  );
}

interface PlayerOddsExpansionProps {
  // Doesn't read `isSelected`; narrowed so selection-independent player arrays flow in.
  player: Omit<SelectablePlayer, 'isSelected'>;
  selectedSportsbooks: string[];
  onLineClick?: (target: OverlayTarget) => void;
  className?: string;
  /** When true, show all markets/books even without EV data (odds-only players) */
  noProjection?: boolean;
  /** User-selected markets from the outer filter; empty = show every market */
  selectedMarkets?: Set<MarketCode>;
  sport?: Sport;
  /** Stamps `data-tour={firstBookButton}` on the first per-book row in the
   *  list, so the walkthrough can spotlight the mobile line-movement entry
   *  point. */
  tourTargetIds?: { firstBookButton?: string };
}

export function PlayerOddsExpansion({ player, selectedSportsbooks, onLineClick, className, noProjection = false, selectedMarkets, sport, tourTargetIds }: PlayerOddsExpansionProps) {
  const teaseEv = useEvTease();
  const catalog = useBookCatalogContext();
  const reduce = useReducedMotion();
  const inQuestionableGame = player.scenario !== undefined;

  // Admin rows say per market why subscribers don't see it (game-exact: a back-to-back sibling's entry never carries it).
  const marketDenote = (market: MarketCode): MarketDenote => {
    const hiddenBy = player.markets[market]?.hiddenBy;
    if (!hiddenBy?.length) return null;
    return hiddenBy.includes('player') ? 'analyst-hidden' : 'admin-only';
  };

  // The outer Markets filter is the sole control over which markets appear.
  // Empty filter = every market (length is then bounded only by the ev>=0 book
  // filter in projection mode; odds-only mode shows all books).
  const marketAllowed = (market: MarketCode) =>
    !selectedMarkets || selectedMarkets.size === 0 || selectedMarkets.has(market);
  const bookVisible = (book: BookLine) =>
    selectedSportsbooks.length === 0 || selectedSportsbooks.includes(book.book);

  // A combo's overlay header shows its parts' projections.
  const { marketProjections } = useMemo(() => aggregateMarketLines(player), [player]);

  // Projection mode (subscribers): one row per market/book/side for ev>=0 books,
  // flattened across every allowed market and sorted by EV descending. The top
  // rows are the player's best bets regardless of which market they belong to.
  const projectionRows = useMemo(() => {
    if (noProjection) return [];
    const rows: Array<{ market: MarketCode; book: BookLine }> = [];
    for (const market of Object.keys(player.markets) as MarketCode[]) {
      if (!marketAllowed(market)) continue;
      const marketData = player.markets[market];
      if (!marketData) continue;
      for (const book of marketData.books || []) {
        if (book.ev == null || book.ev < 0) continue;
        if (!bookVisible(book)) continue;
        rows.push({ market, book });
      }
    }
    rows.sort((a, b) => (b.book.ev ?? 0) - (a.book.ev ?? 0));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.markets, selectedSportsbooks, selectedMarkets, noProjection]);

  // Odds-only mode (non-subscribers / projection-less players): no EV to sort
  // by, so group each market's books (over+under stacked) and order by the
  // canonical market order, then book name.
  const oddsOnlyRows = useMemo(() => {
    if (!noProjection) return [];
    const canonical = sport ? getMarketsForSport(sport) : [];
    const orderOf = (m: MarketCode) => {
      const i = canonical.indexOf(m);
      return i === -1 ? canonical.length : i;
    };
    const rows: Array<{ market: MarketCode; book: string; over?: BookLine; under?: BookLine }> = [];
    for (const market of Object.keys(player.markets) as MarketCode[]) {
      if (!marketAllowed(market)) continue;
      const marketData = player.markets[market];
      if (!marketData) continue;
      const grouped = new Map<string, { over?: BookLine; under?: BookLine }>();
      for (const book of marketData.books || []) {
        if (!bookVisible(book)) continue;
        const g = grouped.get(book.book) ?? {};
        if (book.side === 'over') g.over = book;
        else g.under = book;
        grouped.set(book.book, g);
      }
      for (const [book, sides] of grouped) {
        if (!sides.over && !sides.under) continue;
        rows.push({ market, book, ...sides });
      }
    }
    rows.sort((a, b) => orderOf(a.market) - orderOf(b.market) || (catalog.get(a.book)?.name ?? a.book).localeCompare(catalog.get(b.book)?.name ?? b.book));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.markets, selectedSportsbooks, selectedMarkets, noProjection, sport, catalog]);

  // The rows are sorted by EV, so the first that is its market's computed best line is the player's best.
  const bestRowIdx = projectionRows.findIndex(({ market, book }) => {
    const best = player.markets[market]?.bestLines?.[book.side];
    return best !== undefined && best.book === book.book && best.line === book.line && best.odds === book.odds;
  });
  const hasRows = noProjection ? oddsOnlyRows.length > 0 : projectionRows.length > 0;

  // When the active Markets filter narrows to a market this player doesn't
  // offer, render an empty state in place of the list instead of collapsing —
  // the card stays open so the user can widen the filter again.
  const getEmptyStateText = (): string => {
    const selectedList = selectedMarkets ? [...selectedMarkets] : [];
    if (selectedList.length === 1) return `No ${getMarketFilterName(selectedList[0])} odds for this player`;
    if (selectedList.length > 1) return 'No odds for the selected markets';
    return 'No odds available for this player';
  };
  const emptyStateText = getEmptyStateText();

  return (
    <motion.div
      // NOTE: deliberately NOT animating height. This expansion lives inside a
      // `@tanstack/react-virtual` row whose `measureElement` ResizeObserver
      // re-measures + repositions every row on each height change — animating
      // height 0→auto fires that loop ~12×/open, one frame behind, which is the
      // stutter. Resizing once (height jumps) + a GPU opacity fade keeps the
      // reveal smooth without thrashing the virtualizer.
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0 : 0.18, ease: EASE_PREMIUM }}
      className={cn('overflow-hidden', className)}
    >
      {/* Inner content slides down into place — a compositor-only transform
          (GPU, off main thread) clipped by the parent's overflow-hidden. The
          row height already settled in one measure, so this never re-triggers
          the virtualizer's ResizeObserver. */}
      <motion.div
        initial={{ y: reduce ? 0 : -6 }}
        animate={{ y: 0 }}
        transition={{ duration: reduce ? 0 : 0.22, ease: EASE_PREMIUM }}
        className="border-t border-gray-200 dark:border-border-secondary"
      >
        {/* Scenario indicator: the questionable players out in this row's scenario, or that the model has no projection for it */}
        {player.scenario && (player.scenario.out.length > 0 || !player.scenario.modeled) && (
          <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/40">
            <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
              {player.scenario.modeled
                ? `Projections without ${player.scenario.out.join(', ')}`
                : player.scenario.out.length > 0
                  ? `No projection modeled for ${player.scenario.out.join(' + ')} out`
                  : 'The analyst\'s lineup isn\'t modeled yet'}
            </span>
          </div>
        )}

        {/* Flattened odds list — every allowed market in one list, each row
            self-describing via its market label. Which markets appear is
            dictated entirely by the outer Markets filter. */}
        <div className="px-2 sm:px-3 pt-0.5 pb-1.5 sm:pb-2">
          {teaseEv && hasRows && (
            <div className="pb-2">
              <UnlockEvBanner />
            </div>
          )}
          {!hasRows ? (
            <div className="py-6 text-center text-xs text-gray-400 dark:text-text-tertiary">
              {emptyStateText}
            </div>
          ) : (
          <div className="divide-y divide-gray-100 dark:divide-border-secondary">
            {noProjection ? (
              oddsOnlyRows.map((row, idx) => {
                const isRemoved = (row.over?.isRemoved ?? true) && (row.under?.isRemoved ?? true);
                return (
                  <div
                    key={`${row.market}-${row.book}`}
                    data-tour={idx === 0 ? tourTargetIds?.firstBookButton : undefined}
                    className={cn(
                      "w-full flex items-center justify-between gap-2 py-2 min-h-[44px]",
                      isRemoved && "opacity-50"
                    )}
                  >
                    {/* Left: market over book */}
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      <MarketLabel market={row.market} denote={marketDenote(row.market)} />
                      <BookLabel book={row.book} />
                    </div>
                    {/* Right: over/under stacked */}
                    <div className="flex flex-col items-end gap-0.5 font-mono text-[11px] sm:text-xs flex-shrink-0 tabular-nums">
                      {row.over && <SideOdds side="over" book={row.over} />}
                      {row.under && <SideOdds side="under" book={row.under} />}
                    </div>
                  </div>
                );
              })
            ) : (
              projectionRows.map(({ market, book }, idx) => {
                // The best line: the highest-EV row that is its market's computed best line (the pricer's, with the
                // viewer's eligibility), so a reference book or a thin exchange side is never the one emphasized.
                const isBest = idx === bestRowIdx && projectionRows.length > 1;
                const marketData = player.markets[market];
                return (
                  <button
                    key={`${market}-${book.book}-${book.side}-${idx}`}
                    data-tour={idx === 0 ? tourTargetIds?.firstBookButton : undefined}
                    onClick={() => onLineClick?.(buildOverlayTarget({
                      playerName: player.name,
                      market: getMarketFilterName(market),
                      marketCode: market,
                      imageUrl: player.imageUrl,
                      playerId: player.id,
                      projection: marketData?.projection,
                      pmf: marketData?.pmf,
                      outPlayerIds: player.scenario?.outPlayerIds,
                      bestLines: marketData?.bestLines,
                      // Filter the books handed to the Bet/Share popovers by
                      // visibility only — keeps the chooser in lockstep with the
                      // breakdown list (Reference books are visible and selectable;
                      // best-line eligibility is enforced via the bestLines prop).
                      books: marketData?.books?.filter(bookVisible),
                      projectedMinutes: marketData?.projectedMinutes,
                      team: player.team,
                      opponent: player.game?.opp,
                      commenceTime: player.game?.commenceTime,
                      gameId: player.game?.id,
                      isHome: player.game?.isHome,
                      // PlayerOddsExpansion doesn't compute weather (no slate-level
                      // weatherMap in scope); table-row click path is the only one
                      // that threads weather through. Overlay handles undefined.
                      clickedBook: book.book,
                      marketProjections,
                    }))}
                    className={cn(
                      "w-full flex items-center justify-between gap-2 py-2 min-h-[44px] rounded-md",
                      "transition-colors",
                      onLineClick && "hover:bg-gray-50 dark:hover:bg-bg-tertiary active:bg-gray-100 dark:active:bg-bg-tertiary",
                      book.isRemoved && "opacity-50"
                    )}
                  >
                    {/* Left: market over book */}
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      <MarketLabel market={market} denote={marketDenote(market)} />
                      <BookLabel book={book.book} strong={isBest} className={book.isRemoved ? 'opacity-60' : undefined} />
                    </div>
                    {/* Right: edge over line+odds */}
                    <div className="flex items-center flex-shrink-0">
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={cn(
                            'text-xs sm:text-sm font-bold tabular-nums leading-tight',
                            book.isRemoved ? 'text-muted-foreground' :
                            book.ev != null ? getEvColorClass(book.ev, inQuestionableGame ? 'questionable' : 'default') : 'text-muted-foreground'
                          )}
                        >
                          {book.ev != null
                            ? formatEV(book.ev)
                            : '—'}
                        </span>
                        <div className="flex items-center gap-0.5 sm:gap-1 font-mono text-[11px] sm:text-xs tabular-nums">
                          <span className={cn(
                            "font-medium",
                            book.isRemoved ? "text-muted-foreground" : "text-gray-900 dark:text-text-primary"
                          )}>
                            {book.side === 'over' ? 'o' : 'u'}{book.line.toFixed(1)}
                          </span>
                          <span className="text-gray-400 dark:text-text-secondary">
                            ({formatOdds(book.odds)})
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
